var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import JSZip from 'jszip';
import { loadWatermark } from 'app/assets/brands/Logo';
import { renderToImage as asyncRenderToImage } from 'app/image/rasterize/renderer.async';
import { renderToImage as syncRenderToImage } from 'app/image/rasterize/renderer.sync';
import { EditorActions } from 'app/stores/editor/actions';
import { getProjectState } from 'app/stores/project';
import { getPageRect } from 'app/stores/project/page';
import { getCurrentPage, getPages } from 'app/stores/selectors';
import { hasOffscreenCanvas } from 'app/utils/caniuse';
import { errorNotifier, leaveBreadcrumb } from 'app/utils/error';
import { Version } from 'app/utils/version';
const Consts = { preferSyncRasterizer: true };
export const rasterizeProjectAction = createAsyncThunk('project/rasterize', (opts, { dispatch, getState }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c;
    const { format, generateManifest = false, mangleFilenames = false, compress = true, useStroke = configGet('useWhiteStroke', true), useSyncRasterizer = false, } = opts;
    leaveBreadcrumb(Object.assign({}, opts), '[Rasterize][start] Rasterize project');
    try {
        // collect some info on fontSizes
        const rootStyle = window.getComputedStyle(document.children[0]);
        const rootFontSize = parseFloat((_a = rootStyle.fontSize) !== null && _a !== void 0 ? _a : '16px');
        const projectPreviewStyle = window.getComputedStyle(document.getElementById('project-preview'));
        const projectPreviewFontSize = parseFloat((_b = projectPreviewStyle.fontSize) !== null && _b !== void 0 ? _b : '14px');
        const state = getState();
        const projectState = getProjectState(state);
        const { markers, images } = projectState;
        let pages = getPages(state);
        const strategy = (_c = opts.strategy) !== null && _c !== void 0 ? _c : (pages.length > 1 ? 'all_pages' : 'current_page');
        const multiSlidesExport = strategy === 'all_pages';
        // if only one page was requested filter-out all other pages
        if (!multiSlidesExport) {
            const currentPage = getCurrentPage(state);
            pages = pages.filter(page => page.id === currentPage.id);
        }
        let watermark;
        if (!configGet('hideWatermarkDuringRollout', false)) {
            const { url: watermarkUrl, image: watermarkImage } = yield loadWatermark();
            if (watermarkImage) {
                watermark = {
                    source: { url: watermarkUrl },
                    size: {
                        width: watermarkImage.width,
                        height: watermarkImage.height,
                    },
                    placement: 'bottom-right',
                    offset: { x: 10, y: 10 },
                };
            }
        }
        const renderJobs = pages.map(page => {
            const image = images[page.image];
            if (!image) {
                throw new Error('Missing source image');
            }
            const { width, height } = image.size;
            const markup = {
                blurs: page.blurs.map(id => markers[id]),
                marks: page.marks.map(id => markers[id]),
                spots: page.spots.map(id => markers[id]),
                pins: page.pins.map(id => markers[id]),
                texts: page.texts.map(id => markers[id]),
            };
            const usedImages = markup.marks
                .filter(marker => marker.alias === 'Image')
                .reduce((accum, marker) => {
                const img = images[marker.image];
                if (!img) {
                    throw new Error('Missing source image. Marker: ' + marker.id);
                }
                accum[img.id] = {
                    width: img.size.width,
                    height: img.size.height,
                    source: { url: img.url },
                };
                return accum;
            }, {});
            const contentBounds = getPageRect(page, image);
            const renderJob = {
                markup,
                image: {
                    width,
                    height,
                    source: { url: image.url },
                },
                images: usedImages,
                opts: {
                    useStroke,
                    watermark,
                },
                blurredImage: image.blurredImage
                    ? {
                        width: image.blurredImage.width,
                        height: image.blurredImage.height,
                        source: { url: image.blurredImage.url },
                    }
                    : undefined,
                output: {
                    type: format,
                    bounds: contentBounds,
                    style: page.style ? Object.assign({}, page.style) : undefined,
                },
                fonts: {
                    remBase: rootFontSize,
                    emBase: projectPreviewFontSize,
                },
            };
            return renderJob;
        });
        const renderer = useSyncRasterizer ||
            Boolean(configGet('preferSyncRasterizer', Consts.preferSyncRasterizer)) === true ||
            !hasOffscreenCanvas()
            ? syncRenderToImage
            : asyncRenderToImage;
        const renders = [];
        for (let index = 0; index < renderJobs.length; index += 1) {
            const renderJob = renderJobs[index];
            dispatch(EditorActions.showBusy(multiSlidesExport
                ? {
                    message: `Exporting slide ${index + 1}/${renderJobs.length}`,
                }
                : {
                    message: 'Exporting slide',
                }));
            // eslint-disable-next-line no-await-in-loop
            const renderBlob = yield renderer(renderJob);
            dispatch(EditorActions.hideBusy());
            renders.push({ job: renderJob, output: renderBlob });
        }
        const renderExtension = format.replace('image/', '');
        let rasterizationOutput;
        if (!renders.length) {
            throw new Error('Missing renders');
        }
        // create zip if user requested package
        if (multiSlidesExport) {
            // build ZIP with multiple renders
            const getPageName = mangleFilenames === true
                ? () => `${nanoid()}.${renderExtension}`
                : index => `page_${index + 1}.${renderExtension}`;
            const files = renders.map((render, index) => ({
                name: getPageName(index),
                render,
            }));
            // if requested add manifest with meta data
            let manifest;
            if (generateManifest) {
                const manifestData = {
                    createdAt: new Date().toUTCString,
                    version: Object.assign({}, Version),
                    files: files.map(file => {
                        const { output, job } = file.render;
                        return {
                            name: file.name,
                            size: output.size,
                            type: output.type,
                            width: job.output.bounds.width,
                            height: job.output.bounds.height,
                        };
                    }),
                };
                manifest = {
                    name: 'manifest.json',
                    data: new Blob([JSON.stringify(manifestData)], {
                        type: 'application/json',
                    }),
                };
            }
            // if requested zip it
            if (compress === true) {
                dispatch(EditorActions.showBusy({
                    message: 'Creating project archive',
                }));
                const zip = new JSZip();
                const folder = zip.folder('annotely');
                files.forEach(file => {
                    folder.file(file.name, file.render.output);
                });
                // if requested add manifest with meta data
                if (manifest) {
                    folder.file(manifest.name, manifest.data);
                }
                // generate zip
                const result = yield zip.generateAsync({
                    type: 'blob',
                    compression: 'STORE',
                });
                rasterizationOutput = [
                    {
                        name: 'annotely_images.zip',
                        data: result,
                    },
                ];
                dispatch(EditorActions.hideBusy());
            }
            else {
                rasterizationOutput = files.map(file => {
                    const { output } = file.render;
                    return { name: file.name, data: output };
                });
                if (manifest) {
                    rasterizationOutput.unshift(manifest);
                }
            }
        }
        else {
            // return single render as image
            rasterizationOutput = [
                {
                    name: `annotely_image.${renderExtension}`,
                    data: renders[0].output,
                },
            ];
        }
        leaveBreadcrumb({
            output: !!rasterizationOutput,
        }, '[Rasterize][end] Rasterize project - start');
        return rasterizationOutput;
    }
    catch (err) {
        errorNotifier(err, '[Rasterize][error] Rasterize project');
        dispatch(EditorActions.hideBusy());
        throw err;
    }
}));
